import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Card,
  SimpleGrid,
  Button,
  Spinner,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BackButton from 'components/backButton/BackButton';
import CommonTable from 'components/commonTable/CommonTable';
import { PiPencil } from 'react-icons/pi';
import {
  AssignMerchantsToStrategy,
  ViewPriceStrategyData,
} from '../../../reactRedux/thunks/priceStrategyThunk/priceStrategyThunk';
import useRegions from 'hooks/useRegions';
import MerchantAssignModal from './MerchantAssignModal';
import AssignResultModal from './AssignResultModal';

const PriceStrategyDetail = () => {
  const { priceStrategyDetailId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { regions } = useRegions();
  const { isViewLoading, isAssignLoading, viewPriceStrategyDetails } =
    useSelector((state) => state.PriceStrategySlice);

  const [assignResult, setAssignResult] = useState(null);

  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();
  const {
    isOpen: isResultOpen,
    onOpen: onResultOpen,
    onClose: onResultClose,
  } = useDisclosure();

  const refreshDetail = useCallback(() => {
    dispatch(ViewPriceStrategyData(priceStrategyDetailId));
  }, [dispatch, priceStrategyDetailId]);

  useEffect(() => {
    if (priceStrategyDetailId) {
      refreshDetail();
    }
  }, [refreshDetail, priceStrategyDetailId]);

  const handleAssignSubmit = (selectedIds) => {
    dispatch(
      AssignMerchantsToStrategy(
        priceStrategyDetailId,
        selectedIds,
        (result) => {
          onAssignClose();
          setAssignResult(result);
          onResultOpen();
          refreshDetail();
        },
      ),
    );
  };

  const handleResultClose = () => {
    setAssignResult(null);
    onResultClose();
  };

  const priceStrategy = viewPriceStrategyDetails?.data;
  const merchants = priceStrategy?.merchants || [];
  const columnHelper = createColumnHelper();
  const merchantColumns = [
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'merchantId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Merchant ID
        </Text>
      ),
      cell: (info) => {
        const merchantId = info.getValue();
        return (
          <Text
            fontSize="sm"
            fontWeight="600"
            align="left"
            color="secondaryGray.900"
            cursor={merchantId ? 'pointer' : 'default'}
            onClick={() => {
              if (merchantId) {
                navigate(`/admin/merchants/details/${merchantId}`, {
                  state: {
                    returnUrl: location.pathname,
                    returnUrlState: location.state,
                  },
                });
              }
            }}
          >
            {merchantId ?? 'N/A'}
          </Text>
        );
      },
    }),
    columnHelper.accessor((row) => row?.name, {
      id: 'name',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Name
        </Text>
      ),
      cell: (info) => (
        <Text
          fontSize="sm"
          fontWeight="600"
          align="left"
          color="secondaryGray.900"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.address, {
      id: 'address',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          whiteSpace="nowrap"
        >
          Address
        </Text>
      ),
      cell: (info) => (
        <Text
          fontSize="sm"
          fontWeight="600"
          align="left"
          color="secondaryGray.900"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
  ];

  if (isViewLoading || !priceStrategy) {
    return (
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Flex justifyContent="space-between" mb={4} gap="3">
          <BackButton
            fallbackUrl="/admin/price-strategy"
            label="Back to Price Strategies"
          />
        </Flex>
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" color="primary" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex justifyContent="space-between" mb={4} gap="3">
        <BackButton
          fallbackUrl="/admin/price-strategy"
          label="Back to Price Strategies"
        />
        <Flex gap="3">
          <Button
            py="10px"
            px="15px"
            fontWeight="semibold"
            borderRadius="8px"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            type="button"
            leftIcon={<PiPencil size={22} />}
            onClick={() => {
              window.location.href = `/admin/price-strategy/edit/${priceStrategy.id}`;
            }}
          >
            Edit Price Strategy
          </Button>
        </Flex>
      </Flex>

      {/* Main Details Card */}
      <Flex direction="column" mb={4}>
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="40px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Text fontSize="xl" fontWeight="bold" mb={6}>
            Price Strategy Details
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Region :</Text>
              <Text fontWeight="semibold">
                {priceStrategy.regionId
                  ? regions?.find((r) => r.id === priceStrategy.regionId)
                      ?.label || priceStrategy.regionId
                  : 'N/A'}
              </Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Name :</Text>
              <Text fontWeight="semibold">{priceStrategy.name}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Free Duration :</Text>
              <Text fontWeight="semibold">{priceStrategy.freeDuration}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Days Limit :</Text>
              <Text fontWeight="semibold">{priceStrategy.daysLimit}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Overtime Fees :</Text>
              <Text fontWeight="semibold">{priceStrategy.overtimeFees}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Deposit :</Text>
              <Text fontWeight="semibold">{priceStrategy.deposit}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Price Per Time Interval :</Text>
              <Text fontWeight="semibold">{priceStrategy.price}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Time Interval :</Text>
              <Text fontWeight="semibold">{priceStrategy.priceMinute}</Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Baije Price ID :</Text>
              <Text fontWeight="semibold">
                {priceStrategy.baijePriceId ?? 'N/A'}
              </Text>
            </Flex>
            <Flex
              alignItems={{ sm: 'start', md: 'center' }}
              direction={{ sm: 'column', md: 'row' }}
              gap="10px"
              padding="4px 8px"
              borderLeft={'1px solid #ccc'}
            >
              <Text fontWeight="normal">Day Capped Amount :</Text>
              <Text fontWeight="semibold">{priceStrategy.maxPrice}</Text>
            </Flex>
          </SimpleGrid>
        </Card>
      </Flex>

      {/* Merchant Assign Modal */}
      <MerchantAssignModal
        isOpen={isAssignOpen}
        onClose={onAssignClose}
        regionId={priceStrategy.regionId}
        currentMerchants={merchants}
        onSubmit={handleAssignSubmit}
        isSubmitting={isAssignLoading}
      />

      {/* Assignment Result Modal */}
      <AssignResultModal
        isOpen={isResultOpen}
        onClose={handleResultClose}
        result={assignResult}
      />

      {/* Merchant Details Card */}
      <Flex direction="column">
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="40px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Text fontSize="xl" fontWeight="bold">
              Assigned Venues
            </Text>
            <Flex align="center" gap={3}>
              <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                Merchant Count:{' '}
                {priceStrategy.merchantCount ?? merchants.length}
              </Badge>
              <Button
                size="sm"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                leftIcon={<PiPencil size={16} />}
                onClick={onAssignOpen}
              >
                Edit
              </Button>
            </Flex>
          </Flex>
          <CommonTable
            columns={merchantColumns}
            data={merchants}
            isLoading={isViewLoading}
            keyValue="merchantId"
            dynamicWidth={{
              merchantId: '140px',
              name: '200px',
              address: '300px',
              merchantEmail: '300px',
            }}
          />
        </Card>
      </Flex>
    </Box>
  );
};

export default PriceStrategyDetail;
